import { useState } from 'react';
import { InlineFileInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-file-input';
import {
  type ComponentSize,
  type ComponentVariant,
} from 'src/modules/shared/types/component.type';
import { type CustomFieldEntityType } from 'src/api/generated';
import { useFileUpload } from 'src/modules/shared/hooks/api/custom-fields/files';
import Typography from '@material-tailwind/react/components/Typography';

interface CustomFieldInlineFileInputProps {
  label: string;
  value: string;
  onSave: (value: string) => Promise<void>;
  entityType: CustomFieldEntityType;
  entityId: number;
  disabled?: boolean;
  loading?: boolean;
  variant?: ComponentVariant;
  size?: ComponentSize;
  layout?: 'inline' | 'stacked';
}

export const CustomFieldInlineFileInput: React.FC<
  CustomFieldInlineFileInputProps
> = ({
  label,
  value,
  onSave,
  entityType,
  entityId,
  disabled = false,
  loading = false,
  variant = 'primary',
  size = 'sm',
  layout = 'inline',
}) => {
  const { uploadFile } = useFileUpload();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = async (file: File): Promise<string> => {
    setUploadError(null);
    try {
      const result = await uploadFile({
        file,
        entityType,
        entityId,
      });

      return result.value;
    } catch (error) {
      setUploadError('Failed to upload file. Please try again.');
      throw error;
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <InlineFileInput
        label={label}
        value={value}
        onSave={onSave}
        onFileChange={handleFileChange}
        disabled={disabled}
        loading={loading}
        variant={variant}
        size={size}
        layout={layout}
        onError={(error) => setUploadError(error.message)}
      />
      {uploadError && (
        <Typography className="text-danger-500 text-xs">
          {uploadError}
        </Typography>
      )}
    </div>
  );
};
